import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';

import { IconLibrary, SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { combineLatest, filter, map, Observable } from 'rxjs';
import { Store } from '@ngrx/store';

import { SelectedServiceData } from '@fcom/dapi/interfaces';
import {
  Category,
  FinnairBoundItem,
  FinnairCheckInEligibility,
  FinnairCheckInEligibilityReason,
  FinnairCheckInEligibilityWindow,
  FinnairDocumentType,
  FinnairOrder,
} from '@fcom/dapi/api/models';
import { LanguageService } from '@fcom/ui-translate';
import { CommonBookingFeatureState } from '@fcom/common-booking';
import { LoginStatus, LoginStep } from '@fcom/core-api/login';
import { loginStatus } from '@fcom/core/selectors';
import { finShare } from '@fcom/rx';
import { TzDate } from '@fcom/core/utils';
import { getBookingDestinationSummary } from '@fcom/common/utils/booking.utils';
import { GtmService } from '@fcom/common/gtm';
import { ElementActions, ElementTypes, GaContext } from '@fcom/common';
import { ButtonIcon, ButtonMode, ButtonTheme, IconPosition } from '@fcom/ui-components';
import { LoginActions } from '@fcom/core/actions';
import { passengersMissingCategoryService } from '@fcom/common-booking/utils/common-booking.utils';

import { departureIsAfterJuneFirst2023 } from '../../../../utils';
import { WindowRef } from '../../../../../core';
import {
  hasGoToAirlineCheckInEligibilityForBound,
  hasUnrecoverableEligibilityForBound,
} from '../../../../utils/check-in.utils';

interface PromotionHeader {
  headerTranslationKey: string;
  headerParams?: Record<string, string | number>;
}

interface Promotion {
  header: PromotionHeader;
  contentTranslationKey?: string;
  icon?: ButtonIcon;
  linkTextTranslationKey?: string;
  linkClickAction?: () => void;
  fragmentUrl?: string;
  type: 'info' | 'alert';
}

const TRANSLATION_PREFIX = 'MMB.promotion.';

const getArrivalLocation = (order: FinnairOrder, boundItem: FinnairBoundItem): string =>
  getBookingDestinationSummary(order).find((l) => l.code === boundItem.arrival.locationCode)?.name ??
  boundItem.arrival.locationCode;

const getFPlusHeader = (order: FinnairOrder, nextBound: FinnairBoundItem, dateNow: TzDate): PromotionHeader => ({
  headerTranslationKey: `${TRANSLATION_PREFIX}fplus.header`,
  headerParams: {
    destination: getArrivalLocation(order, nextBound),
    amountOfDays: dateNow.daysTo(new TzDate(nextBound.departure.dateTime)),
  },
});

const isLoggedInOrOnePassengerHasAyFFCard = (order: FinnairOrder, isLoggedIn: boolean): boolean =>
  isLoggedIn || order.passengers.some((pax) => pax?.frequentFlyerCards?.some((ff) => ff.companyCode === 'AY'));

const get48HoursHeader = (order: FinnairOrder, nextBound: FinnairBoundItem): PromotionHeader => ({
  headerTranslationKey: `${TRANSLATION_PREFIX}24hours.header`,
  headerParams: {
    destination: getArrivalLocation(order, nextBound),
  },
});

const HOURS_TO_DEPARTURE_CUTOFF = 48;
const SHORT_CREATION_HOURS = 24;

@Component({
  selector: 'fin-mmb-promotion',
  templateUrl: './promotion.component.html',
  styleUrls: ['./promotion.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class MmbPromotionComponent implements OnInit {
  readonly ButtonMode = ButtonMode;
  readonly ButtonTheme = ButtonTheme;
  readonly IconPosition = IconPosition;

  @Input()
  order$: Observable<FinnairOrder>;

  @Output()
  upsellClicked: EventEmitter<SelectedServiceData> = new EventEmitter<SelectedServiceData>();

  promotion$: Observable<Promotion>;
  eligibleCheckInBound$: Observable<FinnairBoundItem>;

  constructor(
    private store$: Store<CommonBookingFeatureState>,
    private gtmService: GtmService,
    private languageService: LanguageService,
    private windowRef: WindowRef
  ) {}

  ngOnInit(): void {
    this.eligibleCheckInBound$ = this.order$.pipe(
      map((order: FinnairOrder) => {
        const boundId = order.eligibilities?.checkIn?.find(({ isAllowedToUseCheckIn }) => isAllowedToUseCheckIn)?.id;

        return order.bounds.find(({ id }) => id === boundId);
      }),
      finShare()
    );

    this.promotion$ = combineLatest([this.store$.pipe(loginStatus()), this.order$]).pipe(
      map(([loginStatusFromStore, order]) => {
        if (this.shouldShowTicketProblemPromotion(order)) {
          return this.getNoEticketContent(order);
        }

        const dateNow = TzDate.fromMillis(Date.now());

        const insideCheckInWindowEligibility = order.eligibilities.checkIn?.find(
          (eligibility) =>
            eligibility.checkinWindow &&
            new TzDate(eligibility.checkinWindow.start).isBefore(dateNow) &&
            new TzDate(eligibility.checkinWindow.end).isAfter(dateNow)
        );

        if (insideCheckInWindowEligibility?.isAllowedToUseCheckIn === false) {
          if (hasUnrecoverableEligibilityForBound(order, insideCheckInWindowEligibility.id)) {
            return this.getSeeAgentContent(
              order.bounds.find(({ id }) => id === insideCheckInWindowEligibility?.id),
              order
            );
          }
          if (hasGoToAirlineCheckInEligibilityForBound(order, insideCheckInWindowEligibility.id)) {
            return this.getGoToAirlineCheckInContent();
          }
        }

        const isLoggedIn = loginStatusFromStore === LoginStatus.LOGGED_IN;
        const nextCheckInEligibility: FinnairCheckInEligibility = order.eligibilities.checkIn?.find(
          (eligibility) => eligibility.checkinWindow && new TzDate(eligibility.checkinWindow.start).isAfter(dateNow)
        );
        const checkInStartsBound: FinnairBoundItem | undefined = order.bounds.find(
          ({ id }) => id === nextCheckInEligibility?.id
        );

        if (checkInStartsBound) {
          return this.getCheckInOpensPromotion(
            order,
            checkInStartsBound,
            nextCheckInEligibility.checkinWindow,
            dateNow,
            isLoggedIn
          );
        }

        const nextBound = order.bounds.find((b) => new TzDate(b.departure.dateTime).isAfter(dateNow));
        if (!nextBound) {
          return undefined;
        }
        const lessThan48HoursToDeparture =
          dateNow.hoursTo(new TzDate(nextBound.departure.dateTime)) < HOURS_TO_DEPARTURE_CUTOFF;
        if (departureIsAfterJuneFirst2023(nextBound) && isLoggedInOrOnePassengerHasAyFFCard(order, isLoggedIn)) {
          return this.getFPlusChangesPromotion(order, nextBound, dateNow, lessThan48HoursToDeparture);
        }
        if (lessThan48HoursToDeparture) {
          return this.get48HoursBeforePromotion(order, nextBound);
        }
        if (order.eligibilities.serviceCatalog.isAllowedToUse && nextBound) {
          return this.getFPlusLoginPromotion(order, nextBound, isLoggedIn, dateNow);
        }

        return undefined;
      }),
      finShare()
    );
  }

  linkClick(promotion: Promotion): void {
    promotion.linkClickAction();
  }

  private shouldShowTicketProblemPromotion(order: FinnairOrder): boolean {
    const hasEtickets = order.passengers.some(
      (p) => p.etickets.filter((e) => e.type === FinnairDocumentType.ETICKET).length > 0
    );

    const dateNow = TzDate.fromMillis(Date.now());
    const checkInHasTicketProblem = order.eligibilities.checkIn?.some(
      (e) =>
        e.reason === FinnairCheckInEligibilityReason.NOT_ELIGIBLE_TICKET_PROBLEM &&
        e.checkinWindow &&
        new TzDate(e.checkinWindow.start).isBefore(dateNow) &&
        new TzDate(e.checkinWindow.end).isAfter(dateNow)
    );

    return !hasEtickets || checkInHasTicketProblem;
  }

  private getCheckInOpensPromotion(
    order: FinnairOrder,
    nextBound: FinnairBoundItem,
    eligibilityWindow: FinnairCheckInEligibilityWindow,
    dateNow: TzDate,
    isLoggedIn: boolean
  ): Promotion {
    const hasSeatsToSell = passengersMissingCategoryService(Category.SEAT, order, nextBound);
    const hours = dateNow.hoursTo(new TzDate(eligibilityWindow.start));

    return {
      type: 'info',
      header:
        hours >= 1
          ? {
              headerTranslationKey: `${TRANSLATION_PREFIX}checkInOpens.${hours === 1 ? 'inOneHourHeader' : 'header'}`,
              headerParams: {
                destination: getArrivalLocation(order, nextBound),
                hours: hours,
              },
            }
          : {
              headerTranslationKey: `${TRANSLATION_PREFIX}checkInOpens.minutesHeader`,
              headerParams: {
                destination: getArrivalLocation(order, nextBound),
                minutes: dateNow.minutesTo(new TzDate(eligibilityWindow.start)),
              },
            },
      ...(hasSeatsToSell ? this.getSeatContent(nextBound) : this.getFPlusContent(isLoggedIn)),
    };
  }

  private get48HoursBeforePromotion(order: FinnairOrder, nextBound: FinnairBoundItem): Promotion {
    return {
      type: 'info',
      header: get48HoursHeader(order, nextBound),
    };
  }

  private getFPlusChangesPromotion(
    order: FinnairOrder,
    nextBound: FinnairBoundItem,
    dateNow: TzDate,
    lessThan48HoursToDeparture: boolean
  ): Promotion {
    return {
      type: 'info',
      header: lessThan48HoursToDeparture
        ? get48HoursHeader(order, nextBound)
        : getFPlusHeader(order, nextBound, dateNow),
      fragmentUrl: 'MMB.fragments.fplusChangesPromotion.url',
    };
  }

  private getFPlusLoginPromotion(
    order: FinnairOrder,
    nextBound: FinnairBoundItem,
    isLoggedIn: boolean,
    dateNow: TzDate
  ): Promotion {
    return {
      type: 'info',
      header: getFPlusHeader(order, nextBound, dateNow),
      ...this.getFPlusContent(isLoggedIn),
    };
  }

  private getSeatContent(bound: FinnairBoundItem): Partial<Promotion> {
    return {
      contentTranslationKey: `${TRANSLATION_PREFIX}seats.content`,
      linkTextTranslationKey: `${TRANSLATION_PREFIX}seats.linkText`,
      icon: {
        category: IconLibrary.SVG_LIBRARY,
        name: SvgLibraryIcon.SEAT_OR_ECONOMY,
        position: IconPosition.RIGHT,
      },
      linkClickAction: () => {
        this.gtmService.trackElement(
          'mmb-promotion-seats',
          GaContext.MANAGE_BOOKING,
          ElementTypes.BUTTON,
          undefined,
          ElementActions.CLICK
        );
        this.upsellClicked.emit({ category: Category.SEAT, fragmentId: bound.id });
      },
    };
  }

  private getFPlusContent(isLoggedIn: boolean): Partial<Promotion> {
    return {
      contentTranslationKey: `${TRANSLATION_PREFIX}fplus.content`,
      ...(!isLoggedIn && {
        linkTextTranslationKey: `${TRANSLATION_PREFIX}fplus.linkText`,
        icon: { category: IconLibrary.SVG_LIBRARY, name: SvgLibraryIcon.FINNAIR_PLUS, position: IconPosition.RIGHT },
        linkClickAction: () => {
          this.gtmService.trackElement(
            'mmb-promotion-f-plus-login',
            GaContext.MANAGE_BOOKING,
            ElementTypes.BUTTON,
            undefined,
            ElementActions.CLICK
          );
          this.store$.dispatch(LoginActions.setLoginPhaseStep({ step: LoginStep.CREDENTIALS }));
        },
      }),
    };
  }

  private getSeeAgentContent(bound: FinnairBoundItem | undefined, order: FinnairOrder): Promotion {
    const locationCode = bound?.arrival?.locationCode;
    const isFlyToUS = order.locations[locationCode]?.countryCode === 'US';
    const translationPrefix = `${TRANSLATION_PREFIX}seeAgent`;
    return {
      type: 'alert',
      header: { headerTranslationKey: `${translationPrefix}.title` },
      contentTranslationKey: `${translationPrefix}.${isFlyToUS ? 'flyToUS' : 'notFlyToUS'}.content`,
    };
  }

  private getGoToAirlineCheckInContent(): Promotion {
    const translationPrefix = `${TRANSLATION_PREFIX}goToAirlineCheckIn`;
    return {
      type: 'alert',
      header: { headerTranslationKey: `${translationPrefix}.title` },
      contentTranslationKey: `${translationPrefix}.content`,
    };
  }

  private getNoEticketContent(order: FinnairOrder): Promotion {
    const translationPrefix = `${TRANSLATION_PREFIX}noEticket.${order.otherInformation.isFinnairBooking ? 'finnair' : 'nonFinnair'}`;

    if (
      order.eligibilities.checkIn?.find((e) => e.reason === FinnairCheckInEligibilityReason.NOT_ELIGIBLE_TICKET_PROBLEM)
    ) {
      return {
        type: 'alert',
        header: { headerTranslationKey: `${translationPrefix}.checkInOpen.title` },
        contentTranslationKey: `${translationPrefix}.checkInOpen.content`,
        linkTextTranslationKey: order.otherInformation.isFinnairBooking
          ? `${translationPrefix}.checkInOpen.linkText`
          : '',
        linkClickAction: order.otherInformation.isFinnairBooking
          ? () => {
              this.gtmService.trackElement(
                'mmb-promotion-no_eticket-contact_us_now',
                GaContext.MANAGE_BOOKING,
                ElementTypes.BUTTON,
                undefined,
                ElementActions.CLICK
              );
              this.languageService
                .translate(`${translationPrefix}.checkInOpen.link.url`)
                .pipe(filter(Boolean))
                .subscribe((link) => {
                  this.windowRef.nativeWindow.open(link, '_blank');
                });
            }
          : undefined,
      };
    }

    const lessThan24HoursFromCreation = TzDate.now().hoursTo(new TzDate(order.creationDateTime)) < SHORT_CREATION_HOURS;

    if (lessThan24HoursFromCreation) {
      return {
        type: 'info',
        header: { headerTranslationKey: `${translationPrefix}.issuing.title` },
        contentTranslationKey: `${translationPrefix}.issuing.content`,
      };
    }

    return {
      type: 'info',
      header: { headerTranslationKey: `${translationPrefix}.contactUs.title` },
      contentTranslationKey: `${translationPrefix}.contactUs.content`,
      linkTextTranslationKey: order.otherInformation.isFinnairBooking ? `${translationPrefix}.contactUs.linkText` : '',
      linkClickAction: order.otherInformation.isFinnairBooking
        ? () => {
            this.gtmService.trackElement(
              'mmb-promotion-no_eticket-contact_us',
              GaContext.MANAGE_BOOKING,
              ElementTypes.BUTTON,
              undefined,
              ElementActions.CLICK
            );
            this.languageService
              .translate(`${translationPrefix}.contactUs.link.url`)
              .pipe(filter(Boolean))
              .subscribe((link) => {
                this.windowRef.nativeWindow.open(link, '_blank');
              });
          }
        : undefined,
    };
  }
}
